/**
 * Formatting helpers for audit log rows. Status-based actions use fromStatus/toStatus;
 * item_price_updated stores before/after values in details instead.
 */

import { formatCurrency } from './currency';

function money(n) {
  if (n == null || n === '') return null;
  const x = Number(n);
  if (Number.isNaN(x)) return null;
  return `$${x.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatPaymentAmount(details) {
  if (!details) return null;
  const amount = details.amountInCheckCurrency ?? details.amount;
  if (amount == null || amount === '') return null;
  return formatCurrency(amount, details.currency || 'USD');
}

function buildPricePerCaratOnly(details, which) {
  const ppcKey = which === 'from' ? 'previousPricePerCarat' : 'newPricePerCarat';
  const ppc = money(details[ppcKey]);
  if (!ppc) return '';
  return `${ppc}/ct`;
}

function entityId(doc) {
  if (!doc) return null;
  const v = doc._id ?? doc.id;
  return v != null ? String(v) : null;
}

/** Short display label for the audit log target column / mobile card. */
export function formatAuditTarget(log) {
  if (!log) return '—';
  if (log.action === 'payment_created' || log.action === 'payment_cancelled') {
    const d = log.details || {};
    const amount = d.amountInCheckCurrency ?? d.amount;
    if (amount != null && amount !== '') {
      const amountText = formatCurrency(amount, d.currency || 'USD');
      const typePart = d.paymentType ? ` · ${d.paymentType}` : '';
      return `${amountText}${typePart}`;
    }
  }
  if (log.diamond?.certificateNumber) return log.diamond.certificateNumber;
  if (log.diamond?.shape) return `${log.diamond.shape} ${log.diamond.carat || ''}ct`.trim();
  if (log.lot?.shape || log.lot?.measurement) {
    return [log.lot.shape, log.lot.measurement].filter(Boolean).join(' ');
  }
  if (log.details?.certificateNumber) return log.details.certificateNumber;
  if (log.check?.name) return log.check.name;
  if (log.check?._id) return `#${String(log.check._id).slice(-6).toUpperCase()}`;
  if (log.orderItem?._id) return `#${String(log.orderItem._id).slice(-6).toUpperCase()}`;
  return '—';
}

/** Client display name for audit rows (targetUser). */
export function getAuditClientLabel(log) {
  const u = log?.targetUser;
  if (!u) return null;
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.email || null;
}

export function getAuditClientId(log) {
  return entityId(log?.targetUser);
}

/** Short order id (#XXXXXX) for audit rows. */
export function getAuditOrderShortId(log) {
  const id = entityId(log?.order);
  if (!id) return null;
  return `#${id.slice(-6).toUpperCase()}`;
}

export function getAuditOrderId(log) {
  return entityId(log?.order);
}

/**
 * @returns {{ type: 'empty' } | { type: 'text', text: string } | { type: 'chip', status: string }}
 */
export function getAuditFromCellModel(log) {
  if (log.action === 'item_price_updated' && log.details) {
    const text = buildPricePerCaratOnly(log.details, 'from');
    if (text) return { type: 'text', text };
  }
  if (log.action === 'lot_available_carat_updated' && log.details?.previousAvailableCarat != null) {
    return { type: 'text', text: `${log.details.previousAvailableCarat} ct` };
  }
  if (log.action === 'payment_cancelled' && log.details) {
    const text = formatPaymentAmount(log.details);
    if (text) return { type: 'text', text };
  }
  if (log.fromStatus) return { type: 'chip', status: log.fromStatus };
  return { type: 'empty' };
}

/**
 * @returns {{ type: 'empty' } | { type: 'text', text: string } | { type: 'chip', status: string }}
 */
export function getAuditToCellModel(log) {
  if (log.action === 'item_price_updated' && log.details) {
    const text = buildPricePerCaratOnly(log.details, 'to');
    if (text) return { type: 'text', text };
  }
  if (log.action === 'lot_available_carat_updated' && log.details?.newAvailableCarat != null) {
    return { type: 'text', text: `${log.details.newAvailableCarat} ct` };
  }
  if (log.action === 'payment_created' && log.details) {
    const text = formatPaymentAmount(log.details);
    if (text) return { type: 'text', text };
  }
  if (log.toStatus) return { type: 'chip', status: log.toStatus };
  return { type: 'empty' };
}

export { entityId };
